import { memo, useCallback, type ReactNode } from 'react'
import { useAtomValue } from 'jotai'
import { RefreshCw, Sparkles } from 'lucide-react'
import { Button } from '@highlight/ui'
import { summaryDraftAtom } from '@renderer/hooks/audio/summary-draft'
import { regenerateSummary, startSummaryGeneration } from '@renderer/hooks/audio/summary-generation'
import { useStreamingSummaryText } from '@renderer/hooks/audio/use-streaming-summary'
import { useRendererOwnsSummaries, useSummaryGenerationText } from '@renderer/hooks/audio/use-summary-generation'
import { SUMMARY_REVEAL } from '@renderer/components/summary-markdown'
import { useCopySummary } from '@renderer/hooks/use-audio-note-copy'
import { useSmoothStreamingText } from '@renderer/hooks/use-smooth-streaming-text'
import AudioNoteIpcRenderer from '@renderer/ipc/AudioNoteIpc.renderer'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { useClaimFloatingBarCopy } from '../../../atoms'
import GlobalLogger from '@shared/utils/logger/GlobalLogger'
import { SummaryMarkdown, SummaryUpgradeNotice } from '../../../components/meeting/summary'
import { MeetingViewSection } from './section'
import { MeetingSummarySkeleton } from './summary-skeleton'
import { PreparingIndicator } from './preparing-indicator'
import { SummaryEditor } from './summary-editor'
import type { MeetingSummaryState } from '../derive-meeting-summary-state'

const log = GlobalLogger.bind('MeetingSummarySection')

interface MeetingViewSummarySectionProps {
  /** The open tab, so only the panel on screen offers the bar its copy. */
  isActiveTab: boolean
  summary: MeetingSummaryState
  audioNoteId: string | null
}

/**
 * Summary section of the Recap tab, across the post-meeting lifecycle: a
 * "Preparing" skeleton, then streaming markdown, then the finalized summary.
 * Always open and never collapsible: the summary is what this tab is for. Until
 * generation finishes the section owns the follow-ups in its title too,
 * matching the designs.
 */
export function MeetingViewSummarySection({ summary, audioNoteId, isActiveTab }: MeetingViewSummarySectionProps) {
  const isGenerating = summary.phase === 'preparing' || summary.phase === 'streaming'

  return (
    <MeetingViewSection
      icon={<Sparkles />}
      title={summary.phase === 'finalized' || summary.phase === 'live' ? 'Summary' : 'Summary & Follow-Ups'}
      trailing={
        isGenerating ? (
          <PreparingIndicator />
        ) : summary.phase === 'finalized' ? (
          <SummaryCopyClaim summary={summary.text} audioNoteId={audioNoteId} active={isActiveTab} />
        ) : undefined
      }
      expanded
      tourId="meeting-summary"
      className="border-b-0"
    >
      <div className="px-3 pt-2">
        <MeetingSummaryContent summary={summary} audioNoteId={audioNoteId} />
      </div>
    </MeetingViewSection>
  )
}

/**
 * Renderer-owned: generate (or retry) this exact note. Legacy: main regenerates
 * whatever note it currently holds, which is only right in phases that imply
 * this view owns the live singleton, so legacy never offers it on `missing`.
 */
function useGenerateSummary(audioNoteId: string | null, regenerate: boolean): (() => void) | undefined {
  const rendererOwned = useRendererOwnsSummaries()
  const generate = useCallback(() => {
    if (rendererOwned) {
      if (!audioNoteId) {
        return
      }
      log.info(`${regenerate ? 'Regenerating' : 'Generating'} summary from meeting view for audio note ${audioNoteId}`)
      void (regenerate
        ? regenerateSummary(audioNoteId)
        : startSummaryGeneration(audioNoteId, { trigger: 'meeting-view' }))
    } else {
      // Fire-and-forget: the summary streams back through audio-note state.
      log.info('Requesting main-owned summary generation from meeting view')
      void AudioNoteIpcRenderer.generateSummaryForSavedNote().catch((error) => {
        log.error(
          `Failed to request main-owned meeting summary: ${error instanceof Error ? (error.stack ?? error.message) : String(error)}`,
        )
      })
    }
    AmplitudeRenderer.trackEvent('Audio Note Action', {
      action: 'regenerate summary',
      trigger: 'sidepanel meeting view',
    })
  }, [rendererOwned, audioNoteId, regenerate])
  return rendererOwned && !audioNoteId ? undefined : generate
}

function MeetingSummaryContent({ summary, audioNoteId }: Omit<MeetingViewSummarySectionProps, 'isActiveTab'>) {
  const rendererOwned = useRendererOwnsSummaries()
  const regenerate = summary.phase === 'missing' || (summary.phase === 'failed' && summary.reason === 'empty')
  const generate = useGenerateSummary(audioNoteId, regenerate)

  switch (summary.phase) {
    case 'live':
      return <SummaryNotice>The summary is written once the meeting ends.</SummaryNotice>
    case 'preparing':
      return <MeetingSummarySkeleton />
    // One element for both, so a reveal still running when the summary is finalized carries on into it.
    case 'streaming':
      return <SummaryText audioNoteId={audioNoteId} />
    case 'finalized':
      return audioNoteId ? (
        <SummaryEditor audioNoteId={audioNoteId} savedText={summary.text} />
      ) : (
        <SummaryText audioNoteId={audioNoteId} finalText={summary.text} />
      )
    case 'unavailable':
      return summary.reason === 'free' ? (
        <SummaryUpgradeNotice context="meeting-view-summary" />
      ) : (
        <SummaryNotice>
          Enable cloud sync to summarize your meetings. Transcription happens remotely, but your audio is never stored —
          transcripts and summaries are encrypted at rest.
        </SummaryNotice>
      )
    case 'failed':
      return (
        <div className="flex flex-col gap-3">
          {summary.text && <SummaryMarkdown summary={summary.text} isPending={false} />}
          <SummaryNotice tone="danger" onGenerate={generate}>
            {summary.message}
          </SummaryNotice>
        </div>
      )
    case 'missing':
      return (
        <SummaryNotice onGenerate={rendererOwned ? generate : undefined}>
          No summary was generated for this meeting.
        </SummaryNotice>
      )
  }
}

/**
 * Plain greedy wrapping. Prose wraps `pretty` and headings `balance`, which re-break a block's last
 * lines on every word appended, so words already shown would hop between lines as the text streams
 * in. Kept once it has finished, so it reads the same live and reopened.
 */
const GREEDY_WRAP_CLASS = '[&_*]:text-wrap'

/**
 * The summary text, live or finalized. The only subscriber to the streaming
 * text: everything above it reads the derived phase instead, so a chunk
 * arriving re-renders this leaf and nothing else. The stream's bursts are
 * revealed a word at a time, and `SummaryMarkdown` re-parses only the block the
 * reveal is in. Mounted on a finalized summary, it shows it whole and static.
 */
const SummaryText = memo(function SummaryText({
  audioNoteId,
  finalText,
}: {
  audioNoteId: string | null
  /** Set once the summary is finalized; until then the text comes from the stream. */
  finalText?: string
}) {
  const rendererOwned = useRendererOwnsSummaries()
  const rendererOwnedText = useSummaryGenerationText(audioNoteId)
  const legacyText = useStreamingSummaryText()
  const { text, isRevealing } = useSmoothStreamingText(
    finalText ?? (rendererOwned ? rendererOwnedText : legacyText),
    SUMMARY_REVEAL,
  )
  return (
    <SummaryMarkdown summary={text} isPending={finalText === undefined || isRevealing} className={GREEDY_WRAP_CLASS} />
  )
})

function SummaryNotice({
  children,
  tone,
  onGenerate,
}: {
  children: ReactNode
  tone?: 'danger'
  /** Show the "Generate summary" action wired to this handler. */
  onGenerate?: () => void
}) {
  return (
    <div className="flex flex-col items-start gap-2">
      <p className={tone === 'danger' ? 'text-danger-subtle type-prose-sm' : 'text-foreground-secondary type-prose-sm'}>
        {children}
      </p>
      {onGenerate && (
        <Button
          variant="ghost"
          size="md"
          onClick={onGenerate}
          left={<RefreshCw aria-hidden="true" />}
          className="text-foreground-secondary hover:text-foreground"
        >
          Generate summary
        </Button>
      )}
    </div>
  )
}

function SummaryCopyClaim({
  summary,
  audioNoteId,
  active,
}: {
  summary: string
  audioNoteId: string | null
  active: boolean
}) {
  const draft = useAtomValue(summaryDraftAtom(audioNoteId ?? ''))
  // The draft, not the saved text: copying what is on screen is the point.
  const { handleCopy, hasSummary } = useCopySummary(draft?.text ?? summary)
  useClaimFloatingBarCopy(
    active &&
      hasSummary && {
        label: 'Copy summary',
        copy: () => {
          void handleCopy()
          AmplitudeRenderer.trackEvent('Audio Note Action', {
            action: 'copy summary',
            trigger: 'sidepanel meeting view',
          })
        },
      },
  )
  return null
}
